/**
 * Centralized API Endpoints Configuration
 * All API endpoints should be defined here for easy maintenance and consistency
 * 
 * Naming Convention: API_<DOMAIN>_<RESOURCE>_<ACTION>
 * Example: API_AUTH_STANDARD_LOGIN, API_USERS_LIST, API_ROLES_CREATE
 */

import { API_BASE_URL } from '../config/environment';

// Export the API base URL from the robust environment config
export { API_BASE_URL };

// ============================================================================
// AUTHENTICATION ENDPOINTS
// ============================================================================

export const API_AUTH_STANDARD_LOGIN = '/api/v1/auth/standard/login';
export const API_AUTH_STANDARD_RENEW_TOKEN_BY_OTT = '/api/v1/auth/standard/renew-token-by-ott';
export const API_AUTH_GOOGLE_LOGIN = '/api/v1/auth/google/login';
export const API_AUTH_GOOGLE = '/api/v1/auth/google';
export const API_AUTH_REFRESH = '/api/v1/auth/refresh';
export const API_AUTH_REVOKE = '/api/v1/auth/revoke';
export const API_AUTH_PROVIDERS = '/api/v1/auth/providers';
export const API_ADMIN_AUTH_PROVIDERS_GOOGLE = '/api/v1/admin/auth/providers/google';

// ============================================================================
// TOKEN ENDPOINTS
// ============================================================================

export const API_TOKENS_RENEW = '/api/v1/core/identities/tokens/renew-token';

// ============================================================================
// USER ENDPOINTS
// ============================================================================

export const API_USERS_BASE = '/api/v1/core/identities/users';
export const API_USERS_LIST = '/api/v1/core/identities/users';
export const API_USERS_ME = '/api/v1/core/identities/users/me';
export const API_USERS_ME_PERMISSIONS = '/api/v1/core/identities/users/me/permissions';
export const API_USERS_BY_LOGIN_NAME = '/api/v1/core/identities/users/by-login-name';
export const API_USERS_VALIDATE_USERNAME = '/api/v1/core/identities/users/validate-username';
export const API_USERS_CURRENT = '/api/v1/user/current';

// Dynamic user endpoints (use functions)
export const API_USERS_DETAILS = (userId: string | number) => `/api/v1/core/identities/users/${userId}`;
export const API_USERS_UPDATE = (userId: string | number) => `/api/v1/core/identities/users/${userId}`;
export const API_USERS_DELETE = (userId: string | number) => `/api/v1/core/identities/users/${userId}`;
export const API_USERS_ROLES = (userId: string | number) => `/api/v1/core/identities/users/${userId}/roles`;
export const API_USERS_ROLE_REMOVE = (userId: string | number, roleId: string | number) => 
  `/api/v1/core/identities/users/${userId}/roles/${roleId}`;

// ============================================================================
// LOGIN MANAGEMENT ENDPOINTS
// ============================================================================

export const API_LOGINS_BASE = '/api/v1/core/identities/syslogin';
export const API_LOGINS_ADD_STANDARD = '/api/v1/core/identities/syslogin/add-standard';
export const API_LOGINS_ADD_GOOGLE = '/api/v1/core/identities/syslogin/add-google';
export const API_LOGINS_VALIDATE = '/api/v1/core/identities/syslogin/validate-login-name';
export const API_LOGINS_CHANGE_PASSWORD = '/api/v1/core/identities/syslogin/change-password';
export const API_LOGINS_ADMIN_RESET_PASSWORD = '/api/v1/core/identities/syslogin/admin-reset-password';

// Dynamic login endpoints
export const API_LOGINS_REMOVE = (loginId: number) => `/api/v1/core/identities/syslogin/${loginId}`;
export const API_LOGINS_MOVE = (loginId: number) => `/api/v1/core/identities/syslogin/${loginId}/move`;
export const API_LOGINS_UPDATE_STATUS = (loginId: number) => `/api/v1/core/identities/syslogin/${loginId}/status`;

// ============================================================================
// ROLE ENDPOINTS
// ============================================================================

export const API_ROLES_BASE = '/api/v1/core/identities/SysRole';
export const API_ROLES_LIST = '/api/v1/core/identities/SysRole';
export const API_ROLES_CREATE = '/api/v1/core/identities/SysRole';

// Dynamic role endpoints
export const API_ROLES_DETAILS = (roleId: number) => `/api/v1/core/identities/SysRole/${roleId}`;
export const API_ROLES_UPDATE = (roleId: number) => `/api/v1/core/identities/SysRole/${roleId}`;
export const API_ROLES_DELETE = (roleId: number) => `/api/v1/core/identities/SysRole/${roleId}`;
export const API_ROLES_USERS = (roleId: number) => `/api/v1/core/identities/SysRole/${roleId}/users`;

// ============================================================================
// PERMISSION ENDPOINTS
// ============================================================================

export const API_PERMISSIONS_BASE = '/api/v1/core/identities/SysPermission';
export const API_PERMISSIONS_ASSIGN_TO_ROLE = '/api/v1/core/identities/SysPermission/assign-to-role';
export const API_PERMISSIONS_REMOVE_FROM_ROLE = '/api/v1/core/identities/SysPermission/remove-from-role';
export const API_PERMISSIONS_GRANT_TO_USER = '/api/v1/core/identities/SysPermission/grant-to-user';
export const API_PERMISSIONS_DENY_TO_USER = '/api/v1/core/identities/SysPermission/deny-to-user';
export const API_PERMISSIONS_REMOVE_FROM_USER = '/api/v1/core/identities/SysPermission/remove-from-user';

// Dynamic permission endpoints
export const API_PERMISSIONS_USER_LIST = (userId: number) => 
  `/api/v1/core/identities/SysPermission/user/${userId}/permissions`;
export const API_PERMISSIONS_USER_CHECK = (userId: number, permissionCode: string) => 
  `/api/v1/core/identities/SysPermission/user/${userId}/check/${permissionCode}`;

// ============================================================================
// ADMIN PERMISSION MANAGEMENT ENDPOINTS
// ============================================================================

export const API_ADMIN_PERMISSION_MANAGEMENT_BASE = '/api/v1/admin/permission-management';
export const API_ADMIN_PERMISSIONS_LIST = (partitionKey: string) => 
  `/api/v1/admin/permission-management/permissions/${partitionKey}`;
export const API_ADMIN_USER_PERMISSIONS = (partitionKey: string, userId: number) => 
  `/api/v1/admin/permission-management/users/${partitionKey}/${userId}/permissions`;

// ============================================================================
// USER PREFERENCES ENDPOINTS
// ============================================================================

export const API_PREFERENCES_BASE = '/api/v1/core/settings/preferences';
export const API_PREFERENCES_LANGUAGE_UPDATE = '/api/v1/core/settings/preferences/update-language';
export const API_PREFERENCES_LANGUAGE_GET = '/api/v1/core/settings/preferences/get-language';
export const API_PREFERENCES_THEME = '/api/v1/core/settings/preferences/theme';
export const API_PREFERENCES_GET = '/api/v1/user/preferences';
export const API_PREFERENCES_UPDATE = '/api/v1/user/preferences';
export const API_PREFERENCES_LOCATION = '/api/v1/core/settings/preferences/location';

// User app-access tracking (workspace Recents + Favorites)
export const API_USER_APP_ACCESS_MARK_ACCESSED = '/api/v1/core/settings/user-app-access/mark-accessed';
export const API_USER_APP_ACCESS_RECENTS = '/api/v1/core/settings/user-app-access/recents';
export const API_USER_APP_ACCESS_TOGGLE_FAVORITE = '/api/v1/core/settings/user-app-access/toggle-favorite';
export const API_USER_APP_ACCESS_FAVORITES = '/api/v1/core/settings/user-app-access/favorites';

// ============================================================================
// TOOL ENDPOINTS - Weather Forecast
// ============================================================================

export const API_WEATHER_FORECAST_LIST = '/api/v1/tools/weather-forecast';
export const API_WEATHER_FORECAST_REAL = '/api/v1/tools/weather-forecast/real';

// ============================================================================
// TOOL ENDPOINTS - Simple Notes
// ============================================================================

export const API_SIMPLE_NOTES_BASE = '/api/v1/tools/simple-notes';
export const API_SIMPLE_NOTES_CREATE = '/api/v1/tools/simple-notes';
export const API_SIMPLE_NOTES_GET_BY_ID = '/api/v1/tools/simple-notes';
export const API_SIMPLE_NOTES_LIST_BY_USER = '/api/v1/tools/simple-notes/by-user';
export const API_SIMPLE_NOTES_UPDATE = '/api/v1/tools/simple-notes';
export const API_SIMPLE_NOTES_DELETE = '/api/v1/tools/simple-notes';

// ============================================================================
// SYSTEM CONFIGURATION ENDPOINTS
// ============================================================================

export const API_CONFIGURATION_BASE = '/api/v1/core/settings/configuration';
export const API_CONFIGURATION_LIST = '/api/v1/core/settings/configuration';
export const API_CONFIGURATION_CREATE = '/api/v1/core/settings/configuration';
export const API_CONFIGURATION_METADATA = '/api/v1/core/settings/configuration/metadata';

// Dynamic configuration endpoints
export const API_CONFIGURATION_GET_BY_KEY = (key: string) => `/api/v1/core/settings/configuration/${encodeURIComponent(key)}`;
export const API_CONFIGURATION_UPDATE = (key: string) => `/api/v1/core/settings/configuration/${encodeURIComponent(key)}`;
export const API_CONFIGURATION_DELETE = (key: string) => `/api/v1/core/settings/configuration/${encodeURIComponent(key)}`;
export const API_CONFIGURATION_EFFECTIVE = (category: string) => `/api/v1/core/settings/configuration/effective/${encodeURIComponent(category)}`;
export const API_CONFIGURATION_HIERARCHY = (key: string) => `/api/v1/core/settings/configuration/hierarchy/${encodeURIComponent(key)}`;

// ============================================================================
// TENANT ENDPOINTS
// ============================================================================

export const API_TENANTS_BASE = '/api/v1/core/multitenant/tenant';
export const API_TENANTS_LIST = '/api/v1/core/multitenant/tenant';
export const API_TENANTS_CREATE = '/api/v1/core/multitenant/tenant';

export const API_TENANTS_GET = (id: number) => `/api/v1/core/multitenant/tenant/${id}`;
export const API_TENANTS_UPDATE = (id: number) => `/api/v1/core/multitenant/tenant/${id}`;
export const API_TENANTS_DELETE = (id: number) => `/api/v1/core/multitenant/tenant/${id}`;
export const API_TENANTS_GET_USERS = (id: number) => `/api/v1/core/multitenant/tenant/${id}/users`;
export const API_TENANTS_ASSIGN_USER = (id: number) => `/api/v1/core/multitenant/tenant/${id}/users`;
export const API_TENANTS_REMOVE_USER = (id: number, userId: number) => `/api/v1/core/multitenant/tenant/${id}/users/${userId}`;
export const API_TENANTS_GET_SUBSCRIPTIONS = (id: number) => `/api/v1/core/multitenant/tenant/${id}/subscriptions`;
export const API_TENANTS_SUBSCRIBE = (id: number) => `/api/v1/core/multitenant/tenant/${id}/subscriptions`;
export const API_TENANTS_UNSUBSCRIBE = (id: number, subscriptionId: number) => `/api/v1/core/multitenant/tenant/${id}/subscriptions/${subscriptionId}`;

// ============================================================================
// API KEY MANAGEMENT ENDPOINTS
// ============================================================================

export const API_ADMIN_API_KEYS_LIST      = '/api/v1/admin/api-keys';
export const API_ADMIN_API_KEYS_CREATE    = '/api/v1/admin/api-keys';
export const API_ADMIN_API_KEYS_REVOKE    = (id: number) => `/api/v1/admin/api-keys/${id}`;
export const API_ADMIN_API_KEYS_USAGE     = (id: number) => `/api/v1/admin/api-keys/${id}/usage`;
export const API_ADMIN_API_KEYS_ROTATE    = (id: number) => `/api/v1/admin/api-keys/${id}/rotate`;
export const API_ADMIN_API_KEYS_API_USERS  = '/api/v1/admin/api-keys/api-users';
export const API_ADMIN_API_KEYS_SET_LINKED_USER = (id: number) => `/api/v1/admin/api-keys/${id}/linked-user`;
export const API_ADMIN_API_KEYS_SET_TENANT      = (id: number) => `/api/v1/admin/api-keys/${id}/tenant`;
export const API_ADMIN_API_KEYS_TEST            = '/api/v1/admin/api-keys/test';

// ============================================================================
// APP/TOOL REGISTRY ENDPOINTS
// ============================================================================

export const API_ADMIN_APPS_LIST   = '/api/v1/admin/apps';
export const API_ADMIN_APPS_CREATE = '/api/v1/admin/apps';
export const API_ADMIN_APPS_GET        = (id: number) => `/api/v1/admin/apps/${id}`;
export const API_ADMIN_APPS_UPDATE     = (id: number) => `/api/v1/admin/apps/${id}`;
export const API_ADMIN_APPS_DELETE     = (id: number) => `/api/v1/admin/apps/${id}`;
export const API_ADMIN_APPS_SET_ACTIVE = (id: number) => `/api/v1/admin/apps/${id}/active`;

export const API_SYSAPPS_AVAILABLE = '/api/v1/sysapps/available';

// ============================================================================
// USER EVENT LOG ENDPOINTS
// ============================================================================

export const API_USER_EVENT_LOGS_MY   = '/api/v1/core/user-events';
export const API_ADMIN_USER_EVENT_LOGS = '/api/v1/admin/user-events';

// ============================================================================
// CONNECTOR ENDPOINTS
// ============================================================================

export const API_ADMIN_CONNECTORS_CATALOG = '/api/v1/admin/connectors/catalog';
export const API_ADMIN_CONNECTORS_LIST    = '/api/v1/admin/connectors';
export const API_ADMIN_CONNECTORS_CREATE  = '/api/v1/admin/connectors';
export const API_ADMIN_CONNECTORS_GET     = (id: number) => `/api/v1/admin/connectors/${id}`;
export const API_ADMIN_CONNECTORS_UPDATE  = (id: number) => `/api/v1/admin/connectors/${id}`;
export const API_ADMIN_CONNECTORS_DELETE  = (id: number) => `/api/v1/admin/connectors/${id}`;
export const API_ADMIN_CONNECTORS_ACTIVATE   = (id: number) => `/api/v1/admin/connectors/${id}/activate`;
export const API_ADMIN_CONNECTORS_DEACTIVATE = (id: number) => `/api/v1/admin/connectors/${id}/deactivate`;

// ============================================================================
// PROFILE PICTURE ENDPOINTS
// ============================================================================

export const API_PROFILE_PICTURE_UPLOAD       = '/api/v1/core/identities/users/me/profile-picture';
export const API_PROFILE_PICTURE_DELETE       = '/api/v1/core/identities/users/me/profile-picture';
export const API_PROFILE_PICTURE_SELECT_DEFAULT = '/api/v1/core/identities/users/me/profile-picture/default';
export const API_PROFILE_PICTURE_DEFAULTS     = '/api/v1/core/identities/users/profile-picture/defaults';

export const API_ADMIN_PROFILE_PICTURE_DEFAULTS        = '/api/v1/core/admin/profile-picture/defaults';
export const API_ADMIN_PROFILE_PICTURE_DEFAULTS_UPDATE = (id: number) => `/api/v1/core/admin/profile-picture/defaults/${id}`;
export const API_ADMIN_PROFILE_PICTURE_DEFAULTS_DELETE = (id: number) => `/api/v1/core/admin/profile-picture/defaults/${id}`;

// ============================================================================
// TRANSLATION ENDPOINTS
// ============================================================================

export const API_TRANSLATIONS_BASE       = '/api/v1/core/translations';
export const API_TRANSLATIONS_BY_LOCALE  = '/api/v1/core/translations/by-locale';
export const API_TRANSLATIONS_BY_ID      = (id: number) => `/api/v1/core/translations/${id}`;

// ============================================================================
// GEOLOCATION ENDPOINTS
// ============================================================================

export const API_GEOLOCATION_RESOLVE = '/api/v1/core/geolocation/resolve';

// ============================================================================
// THIRD-PARTY EXTERNAL URLS
// ============================================================================

// ============================================================================
// PARTY MANAGER ENDPOINTS
// ============================================================================

// Party CRUD
export const API_PARTY_LIST   = '/api/v1/party/parties';
export const API_PARTY_CREATE = '/api/v1/party/parties';
export const API_PARTY_GET    = (id: number) => `/api/v1/party/parties/${id}`;
export const API_PARTY_UPDATE = (id: number) => `/api/v1/party/parties/${id}`;
export const API_PARTY_DELETE = (id: number) => `/api/v1/party/parties/${id}`;
export const API_PARTY_ACTIVATE   = (id: number) => `/api/v1/party/parties/${id}/activate`;
export const API_PARTY_DEACTIVATE = (id: number) => `/api/v1/party/parties/${id}/deactivate`;

// Party Roles
export const API_PARTY_ROLES_LIST   = (partyId: number) => `/api/v1/party/parties/${partyId}/roles`;
export const API_PARTY_ROLES_ASSIGN = (partyId: number) => `/api/v1/party/parties/${partyId}/roles`;
export const API_PARTY_ROLES_REMOVE = (partyId: number, roleId: number) => `/api/v1/party/parties/${partyId}/roles/${roleId}`;

// Party Addresses
export const API_PARTY_ADDRESSES_LIST       = (partyId: number) => `/api/v1/party/parties/${partyId}/addresses`;
export const API_PARTY_ADDRESSES_CREATE     = (partyId: number) => `/api/v1/party/parties/${partyId}/addresses`;
export const API_PARTY_ADDRESSES_UPDATE     = (partyId: number, addressId: number) => `/api/v1/party/parties/${partyId}/addresses/${addressId}`;
export const API_PARTY_ADDRESSES_DELETE     = (partyId: number, addressId: number) => `/api/v1/party/parties/${partyId}/addresses/${addressId}`;
export const API_PARTY_ADDRESSES_SET_DEFAULT = (partyId: number, addressId: number) => `/api/v1/party/parties/${partyId}/addresses/${addressId}/set-default`;

// Party Identifiers (sm_party_identifier — TLT_LONGCODE / X12_ID / EDIFACT_ID / GLN / DUNS / EORI / VAT_ID)
export const API_PARTY_IDENTIFIERS_LIST   = (partyId: number) => `/api/v1/party/parties/${partyId}/identifiers`;
export const API_PARTY_IDENTIFIERS_CREATE = (partyId: number) => `/api/v1/party/parties/${partyId}/identifiers`;
export const API_PARTY_IDENTIFIERS_UPDATE = (partyId: number, identifierId: number) => `/api/v1/party/parties/${partyId}/identifiers/${identifierId}`;
export const API_PARTY_IDENTIFIERS_DELETE = (partyId: number, identifierId: number) => `/api/v1/party/parties/${partyId}/identifiers/${identifierId}`;

// Party Definitions
export const API_PARTY_ROLE_DEFINITIONS_LIST   = '/api/v1/party/definitions/roles';
export const API_PARTY_ROLE_DEFINITIONS_CREATE = '/api/v1/party/definitions/roles';
export const API_PARTY_ROLE_DEFINITIONS_UPDATE = (id: number) => `/api/v1/party/definitions/roles/${id}`;
export const API_PARTY_ROLE_DEFINITIONS_DELETE = (id: number) => `/api/v1/party/definitions/roles/${id}`;
export const API_PARTY_ROLE_DEFINITIONS_ACTIVATE = (id: number) => `/api/v1/party/definitions/roles/${id}/activate`;
export const API_PARTY_ROLE_DEFINITIONS_DEACTIVATE = (id: number) => `/api/v1/party/definitions/roles/${id}/deactivate`;

export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_LIST   = '/api/v1/party/definitions/address-types';
export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_CREATE = '/api/v1/party/definitions/address-types';
export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_UPDATE = (id: number) => `/api/v1/party/definitions/address-types/${id}`;
export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_DELETE = (id: number) => `/api/v1/party/definitions/address-types/${id}`;
export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_ACTIVATE = (id: number) => `/api/v1/party/definitions/address-types/${id}/activate`;
export const API_PARTY_ADDRESS_TYPE_DEFINITIONS_DEACTIVATE = (id: number) => `/api/v1/party/definitions/address-types/${id}/deactivate`;

// ============================================================================
// EDI ITEM MASTER (sm_edi_item + sm_edi_item_alternate_key)
// Spec: pack-analysis-pack-conversion-ladder, Phase M backend.
// Items are auto-populated by ItemResolver from ASN ingest; no create/delete UI.
// ============================================================================
export const API_EDI_ITEMS_LIST              = '/api/v1/edi/items';
export const API_EDI_ITEMS_DETAIL            = (id: number) => `/api/v1/edi/items/${id}`;
export const API_EDI_ITEMS_ALTERNATE_KEYS    = (id: number) => `/api/v1/edi/items/${id}/alternate-keys`;
export const API_EDI_ITEMS_PATCH             = (id: number) => `/api/v1/edi/items/${id}`;
export const API_EDI_ITEMS_KEY_IDENTIFIERS   = (id: number) => `/api/v1/edi/items/${id}/key-identifiers`;
export const API_EDI_ITEMS_PACK_COMPOSITION  = (id: number) => `/api/v1/edi/items/${id}/pack-composition`;
// item-master-prepack-enhancements: in-scope POs/versions declaring a prepack code.
export const API_EDI_ITEMS_SOURCE_POS        = (id: number) => `/api/v1/edi/items/${id}/source-pos`;
// item-master-prepack-enhancements: per-party EDI settings (GET resolve / PUT upsert).
export const API_EDI_PARTY_SETTINGS          = `/api/v1/edi/party-settings`;
// edi-party-settings-admin: registry-driven list of all setting keys for a scope.
export const API_EDI_PARTY_SETTINGS_LIST     = `/api/v1/edi/party-settings/list`;
// mlinkone-sscc-allocation: SSCC-18 carton-identifier settings admin.
export const API_EDI_SSCC_SETTINGS           = `/api/v1/edi/sscc-settings`;
export const API_EDI_SSCC_SETTING            = (id: number) => `/api/v1/edi/sscc-settings/${id}`;
export const API_EDI_SSCC_SETTING_RANGES     = (id: number) => `/api/v1/edi/sscc-settings/${id}/ranges`;
export const API_EDI_SSCC_RANGE              = (rangeId: number) => `/api/v1/edi/sscc-settings/ranges/${rangeId}`;
export const API_EDI_SSCC_RANGE_SUSPEND      = (rangeId: number) => `/api/v1/edi/sscc-settings/ranges/${rangeId}/suspend`;
export const API_EDI_SSCC_RANGE_MOVE         = (rangeId: number) => `/api/v1/edi/sscc-settings/ranges/${rangeId}/move`;
// mlinkone-sscc-allocation: Save Actual Packing (assign SSCC to a committed list).
export const API_EDI_PACKING_SAVE_ACTUAL     = (packingListId: number) => `/api/v1/edi/packing/${packingListId}/save-actual`;
// pack-analysis-item-master-bulk-pack-info: historical pure-BULK pack patterns.
export const API_EDI_ITEMS_BULK_PACK_PATTERNS = (id: number) => `/api/v1/edi/items/${id}/bulk-pack-patterns`;
// mlinkone-item-master-carton-usage: read-only Actual-Packing carton distribution for one item + pair.
export const API_EDI_ITEMS_CARTON_USAGE      = (id: number) => `/api/v1/edi/items/${id}/carton-usage`;
// mlinkone-packing-evidence-precedence Req 9: clear (reset) an item's learned packing pattern for a pair.
export const API_EDI_ITEMS_PACK_PATTERN      = (id: number) => `/api/v1/edi/items/${id}/pack-pattern`;
// edi-master-data-scoped-self-serve: per-pair self-serve display override.
export const API_EDI_ITEMS_SELF_SERVE_OVERRIDE = (id: number) => `/api/v1/edi/items/${id}/self-serve-override`;
// mlinkone-item-master-create-and-scoped-edit
export const API_EDI_ITEMS_CREATE            = '/api/v1/edi/items';
export const API_EDI_ITEMS_PREVIEW_KEY       = '/api/v1/edi/items/preview-key';
// edi-item-master-bulk-dims-weight-entry: fast bulk dims/weights grid save.
export const API_EDI_ITEMS_BATCH_PATCH       = '/api/v1/edi/items/batch-patch';
export const API_EDI_ITEMS_SUPPRESS          = (id: number) => `/api/v1/edi/items/${id}`;

// ============================================================================
// PACK ANALYSIS UoM LADDER (sm_pac_uom_ladder)
// Spec: pack-analysis-pack-conversion-ladder, Phase D + E backend.
// ============================================================================
export const API_PACK_ANALYSIS_UOM_LADDER_RESOLVE   = '/api/v1/pack-analysis/uom-ladder/resolve';
export const API_PACK_ANALYSIS_UOM_LADDER_LIST      = '/api/v1/pack-analysis/uom-ladder';
export const API_PACK_ANALYSIS_UOM_LADDER_CREATE    = '/api/v1/pack-analysis/uom-ladder';
export const API_PACK_ANALYSIS_UOM_LADDER_SUPERSEDE = (id: number) => `/api/v1/pack-analysis/uom-ladder/${id}/supersede`;
export const API_PACK_ANALYSIS_UOM_LADDER_DELETE    = (id: number) => `/api/v1/pack-analysis/uom-ladder/${id}`;

// ============================================================================
// PACK ANALYSIS PACKAGING RULE PROFILE (sm_pac_packaging_rule_profile)
// Spec: pack-analysis-packaging-rule-profile, Phase D + E backend.
// ============================================================================
export const API_PACK_ANALYSIS_PACKAGING_RULE_RESOLVE   = '/api/v1/pack-analysis/packaging-rule/resolve';
export const API_PACK_ANALYSIS_PACKAGING_RULE_LIST      = '/api/v1/pack-analysis/packaging-rule';
export const API_PACK_ANALYSIS_PACKAGING_RULE_CREATE    = '/api/v1/pack-analysis/packaging-rule';
export const API_PACK_ANALYSIS_PACKAGING_RULE_SUPERSEDE = (id: number) => `/api/v1/pack-analysis/packaging-rule/${id}/supersede`;
export const API_PACK_ANALYSIS_PACKAGING_RULE_DELETE    = (id: number) => `/api/v1/pack-analysis/packaging-rule/${id}`;
export const API_PACK_ANALYSIS_PACKAGING_RULE_APPROVED_CARTONS = (id: number) => `/api/v1/pack-analysis/packaging-rule/${id}/approved-cartons`;

// ============================================================================
// CARTON TYPE CATALOG ADMIN (sm_edi_carton_type)
// Spec: pack-analysis-carton-type-catalog-admin.
// ============================================================================
export const API_PACK_ANALYSIS_CARTON_TYPES_LIST       = '/api/v1/pack-analysis/carton-types';
export const API_PACK_ANALYSIS_CARTON_TYPES_CREATE     = '/api/v1/pack-analysis/carton-types';
export const API_PACK_ANALYSIS_CARTON_TYPES_UPDATE     = (id: number) => `/api/v1/pack-analysis/carton-types/${id}`;
export const API_PACK_ANALYSIS_CARTON_TYPES_DELETE     = (id: number) => `/api/v1/pack-analysis/carton-types/${id}`;
export const API_PACK_ANALYSIS_CARTON_TYPES_REACTIVATE = (id: number) => `/api/v1/pack-analysis/carton-types/${id}/reactivate`;
export const API_PACK_ANALYSIS_CARTON_TYPES_REFERENCES = (id: number) => `/api/v1/pack-analysis/carton-types/${id}/references`;
export const API_PACK_ANALYSIS_CARTON_TYPES_SCOPE_OVERRIDE = (id: number) => `/api/v1/pack-analysis/carton-types/${id}/scope-override`;
// edi-master-data-scoped-self-serve: per-pair self-serve display override.
export const API_PACK_ANALYSIS_CARTON_TYPES_SELF_SERVE_OVERRIDE = (id: number) => `/api/v1/pack-analysis/carton-types/${id}/self-serve-override`;
// edi-carton-type-scoped-physical-attributes: un-adopt a carton from a (buyer,
// supplier) pair (soft-deletes ALL the pair's alias rows). Pair passed as query.
export const API_PACK_ANALYSIS_CARTON_TYPES_REMOVE_SCOPE = (id: number) => `/api/v1/pack-analysis/carton-types/${id}/scope`;

// ============================================================================
// PARTY KEY-COMPOSITION PROFILE (sm_party_canonical_key_composition_profile)
// Spec: pack-analysis-key-composition-profile, Phase E backend.
// ============================================================================
export const API_PARTY_KEY_COMPOSITION_RESOLVE   = '/api/v1/party/key-composition/resolve';
export const API_PARTY_KEY_COMPOSITION_LIST      = '/api/v1/party/key-composition';
export const API_PARTY_KEY_COMPOSITION_CREATE    = '/api/v1/party/key-composition';
export const API_PARTY_KEY_COMPOSITION_SUPERSEDE = (id: number) => `/api/v1/party/key-composition/${id}/supersede`;
export const API_PARTY_KEY_COMPOSITION_DELETE    = (id: number) => `/api/v1/party/key-composition/${id}`;

// ============================================================================
// CODE LISTS ENDPOINTS
// ============================================================================

// Country Code Admin (platform management)
export const API_ADMIN_COUNTRY_CODES_LIST   = '/api/v1/admin/code-lists/countries';
export const API_ADMIN_COUNTRY_CODES_CREATE = '/api/v1/admin/code-lists/countries';
export const API_ADMIN_COUNTRY_CODES_GET    = (id: number) => `/api/v1/admin/code-lists/countries/${id}`;
export const API_ADMIN_COUNTRY_CODES_UPDATE = (id: number) => `/api/v1/admin/code-lists/countries/${id}`;
export const API_ADMIN_COUNTRY_CODES_DELETE = (id: number) => `/api/v1/admin/code-lists/countries/${id}`;
export const API_ADMIN_COUNTRY_CODES_ACTIVATE   = (id: number) => `/api/v1/admin/code-lists/countries/${id}/activate`;
export const API_ADMIN_COUNTRY_CODES_DEACTIVATE = (id: number) => `/api/v1/admin/code-lists/countries/${id}/deactivate`;

// Country Code Public (tenant consumption)
export const API_COUNTRY_CODES_ACTIVE = '/api/v1/code-lists/countries';

// UN/LOCODE Admin (platform management)
export const API_ADMIN_UN_LOCODES_LIST   = '/api/v1/admin/code-lists/un-locodes';
export const API_ADMIN_UN_LOCODES_CREATE = '/api/v1/admin/code-lists/un-locodes';
export const API_ADMIN_UN_LOCODES_GET    = (id: number) => `/api/v1/admin/code-lists/un-locodes/${id}`;
export const API_ADMIN_UN_LOCODES_UPDATE = (id: number) => `/api/v1/admin/code-lists/un-locodes/${id}`;
export const API_ADMIN_UN_LOCODES_DELETE = (id: number) => `/api/v1/admin/code-lists/un-locodes/${id}`;
export const API_ADMIN_UN_LOCODES_ACTIVATE   = (id: number) => `/api/v1/admin/code-lists/un-locodes/${id}/activate`;
export const API_ADMIN_UN_LOCODES_DEACTIVATE = (id: number) => `/api/v1/admin/code-lists/un-locodes/${id}/deactivate`;

// UN/LOCODE Public (tenant consumption)
export const API_UN_LOCODES_ACTIVE = '/api/v1/code-lists/un-locodes';

// IATA Code Admin (platform management)
export const API_ADMIN_IATA_CODES_LIST   = '/api/v1/admin/code-lists/iata-codes';
export const API_ADMIN_IATA_CODES_CREATE = '/api/v1/admin/code-lists/iata-codes';
export const API_ADMIN_IATA_CODES_GET    = (id: number) => `/api/v1/admin/code-lists/iata-codes/${id}`;
export const API_ADMIN_IATA_CODES_UPDATE = (id: number) => `/api/v1/admin/code-lists/iata-codes/${id}`;
export const API_ADMIN_IATA_CODES_DELETE = (id: number) => `/api/v1/admin/code-lists/iata-codes/${id}`;
export const API_ADMIN_IATA_CODES_ACTIVATE   = (id: number) => `/api/v1/admin/code-lists/iata-codes/${id}/activate`;
export const API_ADMIN_IATA_CODES_DEACTIVATE = (id: number) => `/api/v1/admin/code-lists/iata-codes/${id}/deactivate`;

// IATA Code Public (tenant consumption)
export const API_IATA_CODES_ACTIVE = '/api/v1/code-lists/iata-codes';

// ============================================================================
// CHAT ENDPOINTS
// ============================================================================

export const API_CHAT_CONNECTORS      = '/api/v1/core/chat/connectors';
export const API_CHAT_COMPLETIONS     = '/api/v1/core/chat/completions';
export const API_CHAT_COMPACT         = '/api/v1/core/chat/compact';
export const API_CHAT_MY_CONNECTORS   = '/api/v1/core/chat/my-connectors';
export const API_CHAT_MY_CONNECTORS_ID = (id: number) => `/api/v1/core/chat/my-connectors/${id}`;
export const API_CHAT_MY_USAGE        = '/api/v1/core/chat/my-usage';
// BYOK Provider Keys
export const API_CHAT_PROVIDER_KEYS           = '/api/v1/core/chat/provider-keys';
export const API_CHAT_PROVIDER_KEYS_ID        = (id: number) => `/api/v1/core/chat/provider-keys/${id}`;
export const API_CHAT_PROVIDER_KEYS_TEST      = '/api/v1/core/chat/provider-keys/test';
export const API_CHAT_PROVIDER_KEYS_CATALOG   = '/api/v1/core/chat/provider-keys/catalog';
export const API_CHAT_PROVIDER_KEYS_MODELS    = (id: number) => `/api/v1/core/chat/provider-keys/${id}/models`;
export const API_CHAT_PROVIDER_KEYS_MODEL     = (id: number, modelFamily: string) => `/api/v1/core/chat/provider-keys/${id}/models/${encodeURIComponent(modelFamily)}`;
export const API_CHAT_PROVIDER_KEYS_MODEL_UPDATE = (providerKeyId: number, connectorId: number) => `/api/v1/core/chat/provider-keys/${providerKeyId}/models/${connectorId}`;
// Spec F (chat-proactive-greeting), Phase 9 — proactive greeting producer endpoint.
export const API_CHAT_GREETING_OPEN    = '/api/v1/core/chat/greeting/open';
// Chat Quality Foundation — feedback + admin inspector/summary
export const API_CHAT_FEEDBACK        = '/api/v1/core/chat/feedback';
export const API_CHAT_FEEDBACK_ID     = (messageId: string) => `/api/v1/core/chat/feedback/${encodeURIComponent(messageId)}`;
export const API_ADMIN_CHAT_PROMPT_SNAPSHOT = (correlationId: string) => `/api/v1/admin/chat/prompt-snapshots/${encodeURIComponent(correlationId)}`;
export const API_ADMIN_CHAT_FEEDBACK_SUMMARY = '/api/v1/admin/chat/feedback/summary';
// Chat Session History (Spec B) — sessions controller
export const API_CHAT_SESSIONS         = '/api/v1/core/chat/sessions';
export const API_CHAT_SESSION_ID       = (id: number) => `/api/v1/core/chat/sessions/${id}`;
export const API_CHAT_SESSION_TITLE    = (id: number) => `/api/v1/core/chat/sessions/${id}/title`;
export const API_CHAT_SESSION_PIN      = (id: number) => `/api/v1/core/chat/sessions/${id}/pin`;
// Chat User Memory (Spec C) — preferences / recent entities / facts
export const API_CHAT_USER_MEMORY_PREFERENCES        = '/api/v1/core/chat/user-memory/preferences';
export const API_CHAT_USER_MEMORY_PREFERENCE_BY_KEY  = (key: string) => `/api/v1/core/chat/user-memory/preferences/${encodeURIComponent(key)}`;
export const API_CHAT_USER_MEMORY_RECENT_ENTITIES    = '/api/v1/core/chat/user-memory/recent-entities';
export const API_CHAT_USER_MEMORY_RECENT_ENTITY_BY_ID = (id: number) => `/api/v1/core/chat/user-memory/recent-entities/${id}`;
export const API_CHAT_USER_MEMORY_FACTS              = '/api/v1/core/chat/user-memory/facts';
export const API_CHAT_USER_MEMORY_FACT_BY_ID         = (id: number) => `/api/v1/core/chat/user-memory/facts/${id}`;

// ──────────────────────────────────────────────────────────────────────────
// PACK ANALYSIS ENDPOINTS
// ──────────────────────────────────────────────────────────────────────────
export const API_PACK_ANALYSIS_ASN_INGEST          = '/api/v1/pack-analysis/asn/ingest';
export const API_PACK_ANALYSIS_ASN_INGEST_CANCEL   = (clientBatchId: string) => `/api/v1/pack-analysis/asn/ingest/cancel/${encodeURIComponent(clientBatchId)}`;
export const API_PACK_ANALYSIS_ASN_REJECTED        = '/api/v1/pack-analysis/asn/rejected';
export const API_PACK_ANALYSIS_ASN_REPROCESS       = (doc856Id: number) => `/api/v1/pack-analysis/asn/${doc856Id}/reprocess`;
export const API_PACK_ANALYSIS_ASN_REPROCESS_BATCH = '/api/v1/pack-analysis/asn/reprocess-batch';

// Effective per-user PA permissions (spec: pack-analysis-edi-module-permissions Slice 2c).
// Drives workbench tab visibility: union of global + active-party grants.
export const API_PACK_ANALYSIS_MY_PERMISSIONS      = '/api/v1/pack-analysis/my-permissions';

// Effective per-user PO Workbench permissions (mirror of PA my-permissions for ML2).
// Drives ML2 PO section visibility: union of global + active-party edi.po.* grants.
export const API_PO_WORKBENCH_MY_PERMISSIONS       = '/api/v1/mlinkone/po/my-permissions';

// Workbench read API (spec: pack-analysis-workbench-api)
export const API_PACK_ANALYSIS_ASN_LIST            = '/api/v1/pack-analysis/asn';
export const API_PACK_ANALYSIS_ASN_CANONICAL       = (doc856Id: number) => `/api/v1/pack-analysis/asn/${doc856Id}/canonical`;
export const API_PACK_ANALYSIS_PACK_MASTER_LIST    = '/api/v1/pack-analysis/pack-master';
export const API_PACK_ANALYSIS_PACK_MASTER_GET     = (id: number) => `/api/v1/pack-analysis/pack-master/${id}`;
// Workbench dropdown source for the MLinkOne Bridge Tester carton selector.
export const API_PACK_ANALYSIS_CARTON_TYPES        = '/api/v1/pack-analysis/carton-types';
export const API_PACK_ANALYSIS_CARTONIZATION       = '/api/v1/pack-analysis/cartonization';
export const API_PACK_ANALYSIS_CARTONIZATION_MLINKONE_BRIDGE
  = '/api/v1/pack-analysis/cartonization/mlinkone-bridge';
// Spec pack-analysis-constraint-solver-v1 — predicate playground evaluator.
// Pure-function; reuses the live IMixPredicateEvaluator on the BE.
export const API_PACK_ANALYSIS_PREDICATE_PLAYGROUND_EVALUATE
  = '/api/v1/pack-analysis/cartonization/predicate-playground/evaluate';

// Spec pack-planner-why-panel-source-asns — lazy-load source ASN trace
// for Pack Planner Why Panel ⑤ OBSERVATION HISTORY.
export const API_PACK_ANALYSIS_OBSERVATIONS_SOURCE_ASNS
  = '/api/v1/pack-analysis/observations/source-asns';

// ──────────────────────────────────────────────────────────────────────────
// EDI — PARTY-APP ENROLLMENT (shared across pack-analysis / mlinkone / slinkone)
// ──────────────────────────────────────────────────────────────────────────
export const API_EDI_ENROLLMENT_LIST   = '/api/v1/edi/party-app-enrollments';
export const API_EDI_ENROLLMENT_CREATE = '/api/v1/edi/party-app-enrollments';
export const API_EDI_ENROLLMENT_GET    = (id: number) => `/api/v1/edi/party-app-enrollments/${id}`;
export const API_EDI_ENROLLMENT_UPDATE = (id: number) => `/api/v1/edi/party-app-enrollments/${id}`;
export const API_EDI_ENROLLMENT_DISABLE = (id: number) => `/api/v1/edi/party-app-enrollments/${id}`;

// ──────────────────────────────────────────────────────────────────────────
// MLinkOne — PO (Doc850) WORKBENCH (admin-gated; edi.po.*)
// ──────────────────────────────────────────────────────────────────────────
export const API_MLINKONE_PO_LIST      = '/api/v1/mlinkone/po';
export const API_MLINKONE_PO_CANONICAL = (poId: number) => `/api/v1/mlinkone/po/${poId}/canonical`;
export const API_MLINKONE_PO_INGEST    = '/api/v1/mlinkone/po/ingest';
export const API_MLINKONE_PO_REJECTED  = '/api/v1/mlinkone/po/rejected';
export const API_MLINKONE_PO_REPROCESS = (sourceDocId: number) => `/api/v1/mlinkone/po/${sourceDocId}/reprocess`;
export const API_MLINKONE_PO_SHIPMENT_LIST = '/api/v1/edi/po-shipments';

// MLinkOne — CREATE PACKING (PreGen; mlinkone.packing.read / .manage)
// Spec: openspec/changes/mlinkone-packing-workflow. v1 is frontend-first against
// fixtures; these routes are the agreed contract the BE handlers will implement.
export const API_MLINKONE_PACKING_CONTEXT   = '/api/v1/edi/packing/load-context';
export const API_MLINKONE_PACKING_SAVE_DRAFT = '/api/v1/edi/packing/save-draft';
export const API_MLINKONE_PACKING_COMMIT    = '/api/v1/edi/packing/commit';
export const API_MLINKONE_PACKING_CLEAR     = '/api/v1/edi/packing/clear';
export const API_MLINKONE_PACKING_SUGGEST   = '/api/v1/edi/packing/suggest';
export const API_MLINKONE_PACKING_CARTON_EDIT = '/api/v1/edi/packing/carton/edit';
export const API_MLINKONE_PACKING_CARTON_GROUP_EDIT = '/api/v1/edi/packing/carton-group/edit';
export const API_MLINKONE_PACKING_CARTON_DELETE = '/api/v1/edi/packing/carton/delete';

// MLinkOne — PACKING LIST browser (read-only; mlinkone.packing.read)
// Spec: openspec/changes/mlinkone-packing-list-number-and-browser
export const API_MLINKONE_PACKING_LISTS        = '/api/v1/edi/packing-lists';
export const API_MLINKONE_PACKING_LIST_DETAIL  = (packingListId: number) => `/api/v1/edi/packing-lists/${packingListId}`;

// EDI — FIELD MAPPING admin (Doc850 tag → PO; tenant-admin edi.field-mapping.manage)
// Spec: openspec/changes/mlinkone-buyer-po-field-mapping
export const API_EDI_FIELD_MAPPING_RESOLVE      = '/api/v1/edi/field-mapping/resolve';
export const API_EDI_FIELD_MAPPING_LIST_ENTRIES = '/api/v1/edi/field-mapping/list-entries';
export const API_EDI_FIELD_MAPPING_SAVE         = '/api/v1/edi/field-mapping/save';

// EDI — LINE DISPLAY PROFILE (per-buyer packing/PO grid columns)
// resolve = authenticated (feeds the operator grid); list/save = tenant-admin edi.line-display-profile.manage
// Spec: openspec/changes/buyer-line-display-profile
export const API_EDI_LINE_DISPLAY_PROFILE_RESOLVE = '/api/v1/edi/line-display-profile/resolve';
export const API_EDI_LINE_DISPLAY_PROFILE_LIST    = '/api/v1/edi/line-display-profile/list';
export const API_EDI_LINE_DISPLAY_PROFILE_SAVE    = '/api/v1/edi/line-display-profile/save';

// MLinkOne — SUPPLIER COMPANY PROFILE (self-service; edi.company-profile.manage)
// Spec: .kiro/specs/mlinkone-supplier-company-profile/
export const API_MLINKONE_COMPANY_PROFILE = '/api/v1/edi/mlinkone/company-profile';

// ──────────────────────────────────────────────────────────────────────────
// EDI — USER-PARTY ASSIGNMENTS (spec: .kiro/specs/user-party-assignment/)
// ──────────────────────────────────────────────────────────────────────────
export const API_EDI_PARTY_ASSIGNMENTS_LIST   = '/api/v1/edi/identities/user-party-assignments';
export const API_EDI_PARTY_ASSIGNMENTS_CREATE = '/api/v1/edi/identities/user-party-assignments';
export const API_EDI_PARTY_ASSIGNMENTS_GET    = (id: number) => `/api/v1/edi/identities/user-party-assignments/${id}`;
export const API_EDI_PARTY_ASSIGNMENTS_UPDATE = (id: number) => `/api/v1/edi/identities/user-party-assignments/${id}`;
export const API_EDI_PARTY_ASSIGNMENTS_DELETE = (id: number) => `/api/v1/edi/identities/user-party-assignments/${id}`;

export const API_EDI_AFFILIATION_TYPES_LIST   = '/api/v1/edi/identities/user-affiliation-types';
export const API_EDI_AFFILIATION_TYPES_CREATE = '/api/v1/edi/identities/user-affiliation-types';
export const API_EDI_AFFILIATION_TYPES_GET    = (id: number) => `/api/v1/edi/identities/user-affiliation-types/${id}`;
export const API_EDI_AFFILIATION_TYPES_UPDATE = (id: number) => `/api/v1/edi/identities/user-affiliation-types/${id}`;
export const API_EDI_AFFILIATION_TYPES_DELETE = (id: number) => `/api/v1/edi/identities/user-affiliation-types/${id}`;

// PATCH-only user endpoint for updating affiliation_type on an existing user.
export const API_USERS_UPDATE_AFFILIATION = (userId: number) =>
  `/api/v1/core/identities/users/${userId}/affiliation`;

// ──────────────────────────────────────────────────────────────────────────
// EDI — ACTIVE-PARTY TOP-BAR (spec: .kiro/specs/active-party-top-bar/)
// ──────────────────────────────────────────────────────────────────────────
export const API_EDI_MY_BUYERS             = '/api/v1/edi/identities/user-party-assignments/my-buyers';
export const API_EDI_MY_SUPPLIERS          = '/api/v1/edi/identities/user-party-assignments/my-suppliers';
export const API_EDI_ACTIVE_PARTY_DEFAULTS = '/api/v1/edi/settings/active-party-defaults';
export const API_EDI_WORKSPACE_ROLE_ASSIGNMENTS = '/api/v1/edi/identities/workspace-role-assignments';
export const API_EDI_ARCHIVE_SETTING       = '/api/v1/edi/settings/archive';

// ──────────────────────────────────────────────────────────────────────────
// TENANT BRANDING SETTINGS (spec: .kiro/specs/tenant-branding-settings/)
// GET ?tenantId={id} reads the nine branding.* keys with override/inherited
// per-field; PUT ?tenantId={id} body { fields } persists per-field overrides.
// ──────────────────────────────────────────────────────────────────────────
export const API_TENANT_BRANDING_SETTINGS = '/api/v1/branding/settings';

// ──────────────────────────────────────────────────────────────────────────
// GLOBAL BRANDING SETTINGS (spec: openspec/changes/global-branding-settings/)
// System Admin surface for /global branding keys. GET = sys.config.read,
// PUT = sys.config.update (split per design D2). No tenantId on the wire.
// ──────────────────────────────────────────────────────────────────────────
export const API_GLOBAL_BRANDING_SETTINGS = '/api/v1/branding/global-settings';

/** Resolves caller's public IP address (free, no auth). */
export const EXTERNAL_IPIFY_URL = 'https://api.ipify.org?format=json';

/** Nominatim reverse-geocoding: append ?format=json&lat=…&lon=… (free, no auth). */
export const EXTERNAL_NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
